import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useEventListener } from '../../../../hooks/useEventListener';
import { Divider, Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import { assessmentsConfig } from '../../../../store/reducers/externalConfig';
import _ from 'lodash';
import InformationBox from '../../../../components/other/informationBox';
import { agyleAssessmentList } from '../../../../store/reducers/dicts';
import { getStringAgeType, PATIENT_CONTEXT_TYPE } from '../../../../util/patientContextUtil';
import PatientContextButtonRender from './buttons/patientContextButtonRender';
import { ctxPatientAgeType, ctxPatientType, ctxSplitScreen } from '../../../../store/reducers/patientContext';

const MainScreenAssessments = ({ patient, disableSave, container }) => {
    const { t } = useTranslation();

    // Selectors
    const assessmentsCfg = useSelector(assessmentsConfig);
    const assessmentList = useSelector(agyleAssessmentList);
    const patientType = useSelector(ctxPatientType);
    const patientAgeType = useSelector(ctxPatientAgeType);
    const splitScreen = useSelector(ctxSplitScreen);

    const [assessmentListForContext, setAssessmentListForContext] = useState([]);

    const [columnCount, setColumnCount] = useState('1fr 1fr 1fr');

    useEffect(() => {
        switch (patientType) {
            case PATIENT_CONTEXT_TYPE.ED:
            case PATIENT_CONTEXT_TYPE.SDEC:
            case PATIENT_CONTEXT_TYPE.UTC:
                let assessmentFind = assessmentsCfg?.ED?.mandatory?.find(
                    (ass) => ass?.service?.wardsId?.[0] === patient?.admission?.pointOfCare,
                );
                if (!assessmentFind) {
                    assessmentFind = assessmentsCfg?.ED?.mandatory?.find((ass) => ass?.id === 'default');
                }
                prepareAssessmentList(_.uniq(assessmentFind?.assessmentList));
                break;
            case PATIENT_CONTEXT_TYPE.EDAU:
            case PATIENT_CONTEXT_TYPE.INPATIENT: {
                const flat = _.flatMap(assessmentsCfg?.INPATIENT?.mandatory, (ass) => ass?.assessmentList);
                prepareAssessmentList(_.uniq(flat));
                break;
            }

            default:
                setAssessmentListForContext([]);
        }
    }, [patientType, patientAgeType]);

    useEffect(() => {
        getColumnCount();
    }, [splitScreen?.enabled]);

    function getColumnCount() {
        const width = container?.offsetWidth ?? window.innerWidth;
        let columns = '1fr 1fr 1fr';
        if (width < 768) {
            columns = '1fr';
        } else if (width < 1118) {
            columns = '1fr 1fr';
        }
        setColumnCount(columns);
    }

    useEventListener('resize', getColumnCount);

    const changeAge = (assessment) => {
        const getStringAge = getStringAgeType(patientAgeType);
        if (!_.isEmpty(assessment?.ageRestriction)) {
            return assessment?.ageRestriction.includes(getStringAge);
        }
        return true;
    };

    const prepareAssessmentList = (listOfIds) => {
        const assessments = [];
        if (!_.isEmpty(listOfIds)) {
            _.forEach(listOfIds, (id) => {
                const findAss = _.find(assessmentList, { code: id, active: true });
                if (findAss && changeAge(findAss)) assessments.push(findAss);
            });
        }

        // TODO filter by age

        setAssessmentListForContext(assessments);
    };

    const renderAssessments = () => {
        return assessmentListForContext.map((btn) => (
            <PatientContextButtonRender
                key={btn.code}
                type={btn.code}
                patient={patient}
                disableSave={disableSave}
                patientAgeType={patientAgeType}
                isAssessment
            />
        ));
    };

    if (patientType === PATIENT_CONTEXT_TYPE.REVIEW) return null;

    return (
        <Grid container spacing={2} style={{ margin: '0px' }}>
            <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography variant="body1" fontWeight="bold" style={{ fontSize: '1.2rem', paddingLeft: 10 }}>
                    {t('Assessments')}
                </Typography>

                <Divider />
            </Grid>

            {_.isEmpty(assessmentListForContext) && (
                <Grid item xs={12}>
                    <InformationBox type="info">{t('No assessments are configured for this patient type.')}</InformationBox>
                </Grid>
            )}

            {!_.isEmpty(assessmentListForContext) && (
                <div
                    style={{
                        display: 'grid',
                        gap: 16,
                        width: '100%',
                        marginLeft: 16,
                        marginTop: 12,
                        gridTemplateColumns: columnCount,
                    }}
                >
                    {renderAssessments()}
                </div>
            )}
        </Grid>
    );
};

export default MainScreenAssessments;
